"use client";
import useTheme from "@/hooks/ui/useTheme";
import {
    IAchievement,
    ICertificate,
    IEducation,
    IExperience,
    IProject,
    IResource,
    ISkillCategory,
    IStats,
    ITestimonial,
    IUser,
} from "@/types";
import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from "react";

interface AdminContextType {
    user: IUser | null;
    setUser: React.Dispatch<React.SetStateAction<IUser | null>>;
    experiences: IExperience[];
    setExperiences: React.Dispatch<React.SetStateAction<IExperience[]>>;
    educations: IEducation[];
    setEducations: React.Dispatch<React.SetStateAction<IEducation[]>>;
    skills: ISkillCategory[];
    setSkills: React.Dispatch<React.SetStateAction<ISkillCategory[]>>;
    projects: IProject[];
    setProjects: React.Dispatch<React.SetStateAction<IProject[]>>;
    achievements: IAchievement[];
    setAchievements: React.Dispatch<React.SetStateAction<IAchievement[]>>;
    certificates: ICertificate[];
    setCertificates: React.Dispatch<React.SetStateAction<ICertificate[]>>;
    testimonials: ITestimonial[];
    setTestimonials: React.Dispatch<React.SetStateAction<ITestimonial[]>>;
    resources: IResource[];
    setResources: React.Dispatch<React.SetStateAction<IResource[]>>;
    stats: IStats | null;
    setStats: React.Dispatch<React.SetStateAction<IStats | null>>;
    theme: "light" | "dark";
    toggleTheme: () => void;
    loading: boolean;
    showBanner: boolean;
    setShowBanner: React.Dispatch<React.SetStateAction<boolean>>;
    mode: "tech" | "simple";
    toggleMode: () => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const useAdminContext = () => {
    const context = useContext(AdminContext);
    if (!context) {
        throw new Error("useAdminContext must be used within a AdminProvider");
    }
    return context;
};

export const AdminProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<IUser | null>(null);
    const [educations, setEducations] = useState<IEducation[]>([]);
    const [experiences, setExperiences] = useState<IExperience[]>([]);
    const [skills, setSkills] = useState<ISkillCategory[]>([]);
    const [projects, setProjects] = useState<IProject[]>([]);
    const [achievements, setAchievements] = useState<IAchievement[]>([]);
    const [certificates, setCertificates] = useState<ICertificate[]>([]);
    const [testimonials, setTestimonials] = useState<ITestimonial[]>([]);
    const [resources, setResources] = useState<IResource[]>([]);
    const [stats, setStats] = useState<IStats | null>(null);

    const { theme, toggleTheme } = useTheme();
    const [loading, setLoading] = useState(true);

    const [showBanner, setShowBanner] = useState(true);
    const [mode, setMode] = useState<"tech" | "simple">("tech");

    useEffect(() => {
        const fetchPortfolio = async () => {
            try {
                const response = await fetch("/api/portfolio", {
                    cache: "no-store",
                    headers: {
                        "Cache-Control": "no-cache, no-store, must-revalidate",
                        Pragma: "no-cache",
                        Expires: "0",
                    },
                });
                const data = await response.json();
                setUser(data.data.user);
                setEducations(data.data.educations);
                setExperiences(data.data.experiences);
                setSkills(data.data.skills);
                setProjects(data.data.projects);
                setAchievements(data.data.achievements);
                setCertificates(data.data.certificates);
                setTestimonials(data.data.testimonials);
                setStats(data.data.stats);
            } catch (error) {
                console.error("Failed to fetch admin portfolio data", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPortfolio();
    }, []);

    const toggleMode = () => {
        setMode((prev) => (prev === "tech" ? "simple" : "tech"));
    };

    return (
        <AdminContext.Provider
            value={{
                user,
                setUser,
                experiences,
                setExperiences,
                educations,
                setEducations,
                skills,
                setSkills,
                projects,
                setProjects,
                achievements,
                setAchievements,
                certificates,
                setCertificates,
                testimonials,
                setTestimonials,
                resources,
                setResources,
                stats,
                setStats,
                theme,
                toggleTheme,
                loading,
                showBanner,
                setShowBanner,
                mode,
                toggleMode,
            }}
        >
            {children}
        </AdminContext.Provider>
    );
};
